import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowRight, Users, Shield, FileText, Calendar, Bell, TrendingUp, Award, ChevronLeft, ChevronRight, Network } from 'lucide-react';
import { listNews, fetchAllEventsPages, listCompanies, DEFAULT_BASE_URL } from '../services/api';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { getEventStartEnd } from '../utils/eventDateTime';

// Helper function to strip HTML tags from text for previews
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const getApiOrigin = () => {
  try {
    const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getLogoSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${getApiOrigin()}${url}`;
  return url;
};

const normalizeWebsiteUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};


const HERO_YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/Aac02SDWfCg?autoplay=1&mute=1&playsinline=1&controls=1&loop=1&playlist=Aac02SDWfCg&rel=0&modestbranding=1';

const HomePage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [recentNews, setRecentNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [tickerItems, setTickerItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [isMarqueePaused, setIsMarqueePaused] = useState(false);
  
  // Carousel state for Why Choose Us
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Why Choose Us features data - dynamically generated with translations
  const [whyChooseUsFeatures, setWhyChooseUsFeatures] = useState([
    {
      icon: Users,
      title: getText('home.whyChooseUs.feature1.title') || 'Multi-Departmental Coordination',
      desc: getText('home.whyChooseUs.feature1.desc') || 'A strong chain of multi-departmental government coordination',
      color: 'blue'
    },
    {
      icon: Shield,
      title: getText('home.whyChooseUs.feature2.title') || 'Expert Compliance Guidance',
      desc: getText('home.whyChooseUs.feature2.desc') || 'Expert guidance on compliance and minimizing legal risks',
      color: 'emerald'
    },
    {
      icon: TrendingUp,
      title: getText('home.whyChooseUs.feature3.title') || 'Fast Problem Resolution',
      desc: getText('home.whyChooseUs.feature3.desc') || 'Fast and effective representation to solve problems',
      color: 'purple'
    },
    {
      icon: Award,
      title: getText('home.whyChooseUs.feature4.title') || 'Sustainable Business Culture',
      desc: getText('home.whyChooseUs.feature4.desc') || 'Emphasis on sustainability, security, and a lawful business culture',
      color: 'amber'
    },
    {
      icon: FileText,
      title: getText('home.whyChooseUs.feature5.title') || 'Government Benefits Access',
      desc: getText('home.whyChooseUs.feature5.desc') || 'Assistance that ensures full utilization of government schemes and benefits',
      color: 'indigo',
      isHorizontal: false
    },
    {
      icon: Network,
      title: getText('home.whyChooseUs.feature6.title') || 'Industry Networking & Collaboration',
      desc: getText('home.whyChooseUs.feature6.desc') || 'Connect with industry leaders, share best practices, and collaborate on initiatives that drive industrial growth across Maharashtra',
      color: 'blue',
      isHorizontal: true
    },
    {
      icon: Calendar,
      title: getText('home.whyChooseUs.feature7.title') || 'Training & Development Programs',
      desc: getText('home.whyChooseUs.feature7.desc') || 'Comprehensive training programs, workshops, and skill development initiatives to enhance workforce capabilities and industry standards',
      color: 'emerald',
      isHorizontal: true
    }
  ]);

  // Carousel functions for Why Choose Us
  const goToSlide = (index) => {
    setCurrentFeatureIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToPrevious = () => {
    setCurrentFeatureIndex((prev) => (prev - 1 + whyChooseUsFeatures.length) % whyChooseUsFeatures.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToNext = () => {
    setCurrentFeatureIndex((prev) => (prev + 1) % whyChooseUsFeatures.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentFeatureIndex((prev) => (prev + 1) % whyChooseUsFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, whyChooseUsFeatures.length]);

  // Existing useEffect hooks
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
      // Update Why Choose Us features when language changes
      setWhyChooseUsFeatures([
        {
          icon: Users,
          title: getText('home.whyChooseUs.feature1.title') || 'Multi-Departmental Coordination',
          desc: getText('home.whyChooseUs.feature1.desc') || 'A strong chain of multi-departmental government coordination',
          color: 'blue'
        },
        {
          icon: Shield,
          title: getText('home.whyChooseUs.feature2.title') || 'Expert Compliance Guidance',
          desc: getText('home.whyChooseUs.feature2.desc') || 'Expert guidance on compliance and minimizing legal risks',
          color: 'emerald'
        },
        {
          icon: TrendingUp,
          title: getText('home.whyChooseUs.feature3.title') || 'Fast Problem Resolution',
          desc: getText('home.whyChooseUs.feature3.desc') || 'Fast and effective representation to solve problems',
          color: 'purple'
        },
        {
          icon: Award,
          title: getText('home.whyChooseUs.feature4.title') || 'Sustainable Business Culture',
          desc: getText('home.whyChooseUs.feature4.desc') || 'Emphasis on sustainability, security, and a lawful business culture',
          color: 'amber'
        },
        {
          icon: FileText,
          title: getText('home.whyChooseUs.feature5.title') || 'Government Benefits Access',
          desc: getText('home.whyChooseUs.feature5.desc') || 'Assistance that ensures full utilization of government schemes and benefits',
          color: 'indigo',
          isHorizontal: false
        },
        {
          icon: Network,
          title: getText('home.whyChooseUs.feature6.title') || 'Industry Networking & Collaboration',
          desc: getText('home.whyChooseUs.feature6.desc') || 'Connect with industry leaders, share best practices, and collaborate on initiatives that drive industrial growth across Maharashtra',
          color: 'blue',
          isHorizontal: true
        },
        {
          icon: Calendar,
          title: getText('home.whyChooseUs.feature7.title') || 'Training & Development Programs',
          desc: getText('home.whyChooseUs.feature7.desc') || 'Comprehensive training programs, workshops, and skill development initiatives to enhance workforce capabilities and industry standards',
          color: 'emerald',
          isHorizontal: true
        }
      ]);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const news = await listNews();
        const sorted = Array.isArray(news) ? news.sort((a,b) => new Date(b.createdAt||b.date||0) - new Date(a.createdAt||a.date||0)) : [];
        setTickerItems(sorted.slice(0, 8).map(n => ({ id: n._id || n.id, title: n.title })));
        setRecentNews(sorted.slice(0, 4).map(n => ({
          id: n._id || n.id,
          title: n.title,
          description: n.summary || n.description || '',
          date: (n.date || n.createdAt || '').toString().slice(0,10)
        })));
      } catch (e) {
        setTickerItems([]);
      }
    };
    loadNews();
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await fetchAllEventsPages();
        const now = new Date();
        
        // Filter only published, upcoming, and live events
        const upcomingAndLive = Array.isArray(events) 
          ? events.filter(event => {
              // Only show published events
              if (event.isPublished === false) return false;
              
              // Check status first (if available)
              if (event.status === 'Completed' || event.status === 'Cancelled') return false;
              if (event.status === 'Upcoming' || event.status === 'Ongoing') return true;
              
               // Check dates if status is not set
               try {
                 const { start, end } = getEventStartEnd(event);
                 if (!start || !end) return false;

                 // Event is upcoming if start is in the future
                 // Event is live if current time is between start and end
                 return start >= now || (now >= start && now <= end);
               } catch (e) {
                 return false;
               }
             })
          : [];
        
        // Sort by start date (ascending - earliest first, so upcoming events appear first)
        const sorted = upcomingAndLive.sort((a, b) => {
          try {
            const { start: startA } = getEventStartEnd(a);
            const { start: startB } = getEventStartEnd(b);
            if (!startA || !startB) return 0;
            return startA - startB; // Ascending: earliest events first
          } catch (e) {
            return 0;
          }
        });
        
        setUpcomingEvents(sorted.slice(0, 4).map(e => ({
          id: e._id || e.id,
          title: e.title,
          description: e.description || '',
          date: (e.date || e.startDate || '').toString().slice(0,10)
        })));
      } catch (e) {
        setUpcomingEvents([]);
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await listCompanies();
        const list = Array.isArray(data) ? data : (data?.companies || []);
        const active = list.filter(company => company && company.isActive !== false);
        const sorted = active.sort((a, b) => {
          const orderA = Number(a.displayOrder || 0);
          const orderB = Number(b.displayOrder || 0);
          if (orderA !== orderB) return orderA - orderB;
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        setCompanies(sorted);
      } catch (e) {
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);


  return (
    <div className="min-h-screen bg-white" style={{ overflowX: 'hidden', width: '100%' }}>

      {/* Home Video Hero Section */}
      <section className="home-video-hero" aria-label="IFM video hero section">
        <div className="video-container">
          <iframe
            src={HERO_YOUTUBE_EMBED_URL}
            title="Industrial Federation of Maharashtra Introduction Video"
            loading="lazy"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
          <div className="home-video-overlay" aria-hidden="true" />
          <div className="home-video-content">
            <div className="home-video-content-inner">
              <h1 className="home-video-title">
                {getText('home.title')}
              </h1>
              <p className="home-video-subtitle">
                {getText('home.subtitle')}
              </p>
              <div className="home-video-actions">
                <Link to="/services" className="home-video-btn home-video-btn-primary">
                  <span>{getText('home.exploreSupport')}</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/about" className="home-video-btn home-video-btn-secondary">
                  <span>{getText('home.learnMore')}</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About IFM Section */}
      <section className="pb-12 bg-white home-about-section">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6" style={{ marginTop: 0 }}>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2" style={{ marginTop: 0 }}>
              {getText('home.about.title') || 'About IFM'}
            </h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="w-full">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 md:p-8 lg:p-10 shadow-sm border border-gray-200">
              <div className="text-center space-y-4 max-w-4xl mx-auto">
                <div className="space-y-4 text-base md:text-lg text-gray-700 leading-relaxed">
                  <p>
                    {getText('home.about.federation.desc1') || 'The Industrial Federation is a professional and reliable organization that works to resolve the regulatory, operational, and infrastructure challenges faced by industries. We act as a crucial coordinating link between industries and various government departments, ensuring that industrial operations run smoothly, legally, and sustainably.'}
                  </p>
                  <p className="font-medium">
                    {getText('home.about.federation.desc2') || 'Our primary objective is to create a favorable and fear-free environment for industries, and to promote responsible industrial practices such as environmental protection, worker safety, and resource management.'}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-center pt-2 border-t border-gray-200">
                <Link 
                  to="/about" 
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] font-semibold"
                >
                  <span>{getText('home.about.knowMore') || 'Know More'}</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* President Message Section - Centered Heading, Same Styling */}
      <section className="section py-8 md:py-10 lg:py-12 bg-gray-50">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          {/* Centered Heading */}
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {getText('home.president.title')}
            </h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 items-stretch">
            {/* President Message - Left Side */}
            <div className="lg:col-span-2 order-2 lg:order-1 flex">
              <div className="bg-white rounded-lg p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm w-full flex flex-col">
                <div className="flex-1 space-y-3 md:space-y-4">
                  <p className="text-base md:text-lg text-gray-800 leading-relaxed font-medium text-justify">
                    {getText('home.president.message1')}
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getText('home.president.message2').replace(/Industrial Federation/g, '<strong>Industrial Federation</strong>')) }} />
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify">
                    {getText('home.president.message3').includes(': ') ? (
                      <>
                        {getText('home.president.message3').split(': ')[0]}: <em>{getText('home.president.message3').split(': ')[1]}</em>
                      </>
                    ) : (
                      getText('home.president.message3')
                    )}
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getText('home.president.message4').replace(/Industrial Federation/g, '<strong>Industrial Federation</strong>')) }} />
                  <p className="text-base md:text-lg text-gray-800 leading-relaxed font-semibold text-center bg-primary-50 py-3 px-4 md:py-3.5 md:px-5 rounded-lg">
                    {getText('home.president.message5')}
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getText('home.president.message6').replace(/(Pollution Control Authorities|DISH|MIDC|MSEDCL|DIC|Labour Department)/g, '<strong>$1</strong>')) }} />
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify">
                    {getText('home.president.message7')}
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getText('home.president.message8').replace(/Industrial Federation/g, '<strong>Industrial Federation</strong>')) }} />
                  <p className="text-base md:text-lg text-gray-800 leading-relaxed font-semibold text-justify">
                    {getText('home.president.message9')}
                  </p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <p className="text-base md:text-lg font-semibold text-gray-900">
                    {getText('home.president.name')}
                  </p>
                  <p className="text-base md:text-lg text-gray-600">
                    {getText('home.president.position')}
                  </p>
                  <p className="text-sm md:text-base text-gray-500 font-bold">
                    Director - SSDK Project Solutions Pvt Ltd
                  </p>
                </div>
              </div>
            </div>
            
            {/* President Image - Right Side - Enhanced UI/UX */}
            <div className="lg:col-span-1 order-1 lg:order-2 flex">
              <div className="bg-white rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-lg w-full flex flex-col">
                <div className="text-center flex-1 flex flex-col">
                  {/* Image Container with Decorative Frame */}
                  <div className="mb-4 sm:mb-5 flex justify-center">
                    <div className="relative group">
                      {/* Decorative Background Circle */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl sm:rounded-2xl blur-lg sm:blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                      {/* Image Frame */}
                      <div className="relative bg-gradient-to-br from-primary-50 to-white p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl transform group-hover:scale-105 transition-transform duration-300">
                        <img 
                          src="/images/4.jpg"
                          alt={getText('home.president.name')} 
                          className="w-full max-w-[200px] sm:max-w-[240px] md:max-w-xs mx-auto rounded-lg sm:rounded-xl object-cover shadow-md sm:shadow-lg border-2 sm:border-4 border-white"
                          style={{ maxHeight: '280px' }}
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Name and Title */}
                  <div className="flex-1 flex flex-col justify-start">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {getText('home.president.name')}
                    </h3>
                    <div className="w-16 md:w-20 h-1 bg-primary-600 mx-auto mb-3 rounded-full"></div>
                    <p className="text-base md:text-lg font-semibold text-primary-600 mb-2">
                      {getText('home.president.position')}
                    </p>
                    <p className="text-sm md:text-base text-gray-500 font-bold mb-3">
                      Director - SSDK Project Solutions Pvt Ltd
                    </p>
                    
                    {/* Additional Info - Connected to details */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="space-y-3">
                        {getText('home.president.experience') && (
                          <div>
                            <p className="text-sm md:text-base font-semibold text-gray-800 mb-1.5">
                              {getText('home.president.experienceLabel') || 'Experience'}
                            </p>
                            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                              {getText('home.president.experience')}
                            </p>
                          </div>
                        )}
                        {getText('home.president.background') && (
                          <div>
                            <p className="text-sm md:text-base font-semibold text-gray-800 mb-1.5">
                              {getText('home.president.backgroundLabel') || 'Background'}
                            </p>
                            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                              {getText('home.president.background')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News & Events Section */}
      <section className="section py-16 bg-gray-50">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title">{getText('home.recentNewsTitle')}</h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full"></div>
            <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">{getText('home.recentNewsSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 force-4-cols gap-6">
            {recentNews.map((news) => (
              <div key={news.id} className="group">
                <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-primary-600 p-2 rounded-lg">
                      <Bell className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs text-gray-500 font-medium">{news.date}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2 text-lg">{news.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">{news.description}</p>
                  <div className="flex items-center justify-between">
                    <Link 
                      to={`/news/${news.id}`}
                      className="group/link inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-sm font-semibold transition-all duration-200 hover:gap-2"
                    >
                      <span>{getText('home.readMore')}</span>
                      <span className="transition-transform duration-200 group-hover/link:translate-x-1">→</span>
                    </Link>
                    <div className="bg-primary-50 px-2 py-1 rounded-full">
                      <span className="text-xs text-primary-700 font-medium">{getText('home.news.label')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {upcomingEvents.map((event) => (
              <div key={event.id} className="group">
                <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-emerald-300 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-600 p-2 rounded-lg">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs text-gray-500 font-medium">{event.date}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2 text-lg">{event.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                    {stripHtmlTags(event.description)}
                  </p>
                  <div className="flex items-center justify-between">
                    <Link 
                      to={`/events/${event.id}`}
                      className="group/link inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 text-sm font-semibold transition-all duration-200 hover:gap-2"
                    >
                      <span>{getText('home.learnMore')}</span>
                      <span className="transition-transform duration-200 group-hover/link:translate-x-1">→</span>
                    </Link>
                    <div className="bg-emerald-50 px-2 py-1 rounded-full">
                      <span className="text-xs text-emerald-700 font-medium">{getText('home.event.label')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-center gap-3 mt-12 mb-4 flex-wrap px-4">
            <Link 
              to="/news" 
              className="group relative inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:text-gray-900 px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-primary-500/40 text-sm sm:text-base overflow-hidden hover:bg-white border border-transparent hover:border-primary-600"
            >
              <span className="relative z-10 whitespace-nowrap">{getText('home.viewAllNews') || 'View All News'}</span>
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-gray-900 relative z-10" />
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></span>
            </Link>
            <Link 
              to="/events" 
              className="group relative inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:text-gray-900 px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-emerald-500/40 text-sm sm:text-base overflow-hidden hover:bg-white border border-transparent hover:border-emerald-600"
            >
              <span className="relative z-10 whitespace-nowrap">{getText('home.viewAllEvents') || 'View All Events'}</span>
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-gray-900 relative z-10" />
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Carousel */}
      <section className="section py-12 sm:py-16 md:py-20 bg-gray-100">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {getText('home.whyChooseUs.title') || 'Why Choose Us?'}
            </h2>
           <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full"></div>
            <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
              {getText('home.whyChooseUs.subtitle') || 'We provide comprehensive support and expert guidance to help your business thrive in Maharashtra\'s industrial landscape.'}
            </p>
          </div>
          
          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto">
            {/* Navigation Buttons */}
            <button
              onClick={goToPrevious}
              className="absolute left-0 sm:-left-4 md:-left-12 lg:-left-16 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 sm:p-3 md:p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-gray-50 border-2 border-gray-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={getText('carousel.previous')}
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-gray-700" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-0 sm:-right-4 md:-right-12 lg:-right-16 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 sm:p-3 md:p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-gray-50 border-2 border-gray-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={getText('carousel.next')}
            >
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-gray-700" />
            </button>

            {/* Carousel Wrapper */}
            <div className="overflow-hidden rounded-xl sm:rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentFeatureIndex * 100}%)` }}
              >
                {whyChooseUsFeatures.map((feature, index) => {
                  const IconComponent = feature.icon;
                  const colorConfig = {
                    blue: { bg: 'bg-blue-600', border: 'border-blue-200', text: 'text-blue-600' },
                    emerald: { bg: 'bg-emerald-600', border: 'border-emerald-200', text: 'text-emerald-600' },
                    purple: { bg: 'bg-purple-600', border: 'border-purple-200', text: 'text-purple-600' },
                    amber: { bg: 'bg-amber-600', border: 'border-amber-200', text: 'text-amber-600' },
                    indigo: { bg: 'bg-indigo-600', border: 'border-indigo-200', text: 'text-indigo-600' }
                  };
                  const colors = colorConfig[feature.color] || colorConfig.blue;
                  
                  return (
                    <div key={index} className="min-w-full px-2 sm:px-3 md:px-4">
                      <div className={`bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 md:p-10 text-center border-2 ${colors.border} shadow-sm h-full flex flex-col items-center justify-center min-h-[280px] sm:min-h-[320px] md:min-h-[350px]`}>
                        <div className={`${colors.bg} rounded-full w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center mb-4 sm:mb-5 md:mb-6 transition-transform duration-300`}>
                          <IconComponent className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
                        </div>
                        <h3 className={`text-xl sm:text-2xl md:text-3xl font-bold ${colors.text} mb-3 sm:mb-4`}>
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto px-2 sm:px-0">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center items-center gap-2 mt-5 sm:mt-6 md:mt-8">
              {whyChooseUsFeatures.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentFeatureIndex
                      ? 'bg-primary-600 w-6 sm:w-8'
                      : 'bg-gray-300 w-2 hover:bg-gray-400'
                  }`}
                  aria-label={`${getText('carousel.goToSlide')} ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Companies Tied Up With Us */}
      <section className="section py-16 bg-white">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {getText('home.companies.title') || 'Companies Tied Up With Us'}
            </h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full"></div>
            <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
              {getText('home.companies.subtitle') || 'Trusted industry partners who collaborate with IFM on programs, training, and initiatives.'}
            </p>
             <div className="flex justify-center mt-4">
            <Link
              to="/partners"
              className="group inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:text-gray-900 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-primary-500/30 border border-transparent hover:border-primary-600"
            >
              <span>{getText('home.companies.viewAll') || 'View All Partners'}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          </div>

          {companiesLoading ? (
            <div className="companies-marquee companies-marquee-static">
              <div className="companies-marquee-track">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="company-card company-card-skeleton">
                    <div className="company-logo-wrap skeleton-box" />
                    <div className="company-details">
                      <div className="skeleton-line" />
                      <div className="skeleton-line short" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : companies.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center">
              <p className="text-lg font-semibold text-gray-800 mb-2">
                {getText('home.companies.emptyTitle') || 'Companies coming soon'}
              </p>
              <p className="text-sm text-gray-600">
                {getText('home.companies.emptyDesc') || 'We are onboarding partner companies and will showcase them here shortly.'}
              </p>
            </div>
          ) : (() => {
            const baseItems = [];
            while (baseItems.length < Math.max(8, companies.length)) {
              baseItems.push(...companies);
            }
            const trimmedBase = baseItems.slice(0, Math.max(8, companies.length));
            const marqueeItems = [...trimmedBase, ...trimmedBase];
            const marqueeDuration = Math.max(22, trimmedBase.length * 3.5);

            const handleTouchStart = () => setIsMarqueePaused(true);
            const handleTouchEnd = () => {
              setTimeout(() => setIsMarqueePaused(false), 1200);
            };

            return (
              <div
                className={`companies-marquee ${isMarqueePaused ? 'companies-marquee-paused' : ''}`}
                style={{ '--marquee-duration': `${marqueeDuration}s` }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
              >
                <div className="companies-marquee-track" aria-label="Partner companies marquee">
                  {marqueeItems.map((company, index) => {
                    const logoSrc = getLogoSrc(company.logo || company.logoUrl || '');
                    const websiteUrl = normalizeWebsiteUrl(company.websiteUrl || '');
                    const CardWrapper = websiteUrl ? 'a' : 'div';
                    const wrapperProps = websiteUrl
                      ? {
                        href: websiteUrl,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        'aria-label': `${company.name} website`
                      }
                      : {};

                    return (
                      <CardWrapper
                        key={`${company._id || company.id || company.name}-${index}`}
                        className="company-card"
                        {...wrapperProps}
                      >
                        <div className="company-logo-wrap">
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt={`${company.name} logo`}
                              className="company-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="company-logo-fallback">
                              {company.name?.charAt(0) || 'C'}
                            </span>
                          )}
                        </div>
                        <div className="company-details">
                          <p className="company-name">{company.name}</p>
                          {company.tagline && (
                            <p className="company-tagline">{company.tagline}</p>
                          )}
                        </div>
                      </CardWrapper>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </section>
    </div>
  );
};

export default HomePage; 
